/* import __COLOCATED_TEMPLATE__ from './audience-selector-wrapper.hbs'; */
/* RESPONSIBLE TEAM: team-workflows */
/* eslint-disable @intercom/intercom/no-bare-strings */

import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { action } from '@ember/object';
// @ts-ignore no type declaration available for ember-copy
import { copy } from 'ember-copy';
import type IntlService from 'embercom/services/intl';
import type Store from '@ember-data/store';
import {
  AUDIENCE_MODES,
  AudienceModeManager,
  type AudienceMode,
} from 'embercom/utils/workflows/audience-mode-manager';
import { type AttributeGroupList } from 'embercom/objects/visual-builder/configuration/editor';
import { TargetContext } from 'embercom/objects/operator/configuration/custom-bot';
import { AttributeGroupBuilder } from 'embercom/utils/workflows/attribute-group-builder';
import { computeSelectedTargetContexts } from 'embercom/objects/operator/configuration/target-context-config';
import {
  audienceRoles,
  audienceNames,
} from 'embercom/models/data/matching-system/matching-constants';

const CUSTOM_BOT = {
  INBOUND: 'Inbound',
  BUTTON: 'Button',
  OUTBOUND: 'Outbound',
};

const PREDICATE_PATHS = {
  PREDICATES: 'predicateGroup.predicates',
  ROLE_PREDICATES: 'rolePredicateGroup.predicates',
} as const;

interface Signature {
  Element: HTMLDivElement;
  Args: {
    ruleset: $TSFixMe;
    config: $TSFixMe;
    customBot: $TSFixMe;
    isDisabled?: boolean;
  };
}

export default class WorkflowAudienceSelectorWrapper extends Component<Signature> {
  @service declare appService: any;
  @service declare attributeService: any;
  @service declare intl: IntlService;
  @service declare store: Store;

  private audienceModeManager: AudienceModeManager;

  get ruleset() {
    return this.args.ruleset;
  }

  get app() {
    return this.appService.app;
  }

  get targetContexts(): TargetContext[] {
    if (this.args.customBot?.type) {
      let botType = this.args.customBot.type;
      if (
        botType === CUSTOM_BOT.BUTTON ||
        botType === CUSTOM_BOT.INBOUND ||
        botType === CUSTOM_BOT.OUTBOUND
      ) {
        return [];
      }
    }

    if (this.args.customBot?.triggerPredicateGroup) {
      let selectedContexts = computeSelectedTargetContexts(
        this.args.customBot.triggerPredicateGroup,
      );
      if (selectedContexts.length > 0) {
        return selectedContexts;
      }
    }

    return [TargetContext.Conversation];
  }

  private get hasTrackerTickets(): boolean {
    return (
      this.targetContexts.length > 0 &&
      this.targetContexts.some((context) => context === TargetContext.TrackerTicket)
    );
  }

  get selectedSegmentIds(): number[] {
    if (!this.ruleset?.segmentIds) {
      return [];
    }

    if (this.hasTrackerTickets) {
      return [];
    }

    return [...this.ruleset.segmentIds];
  }

  get computedAllowedUserRoles(): string[] {
    // Check if this workflow has any ticket-related target contexts
    let hasTicketContexts =
      this.targetContexts.length > 0 &&
      this.targetContexts.some(
        (context) =>
          context === TargetContext.CustomerTicket ||
          context === TargetContext.BackOfficeTicket ||
          context === TargetContext.TrackerTicket,
      );

    // If no ticket contexts, show all roles (user, lead, visitor)
    if (!hasTicketContexts) {
      return [
        audienceNames[audienceRoles.users],
        audienceNames[audienceRoles.leads],
        audienceNames[audienceRoles.visitors],
      ];
    }

    // For ticket workflows: Tracker has no roles, others have user+lead only (no visitor)
    return this.hasTrackerTickets
      ? []
      : [audienceNames[audienceRoles.users], audienceNames[audienceRoles.leads]];
  }

  get currentAudienceMode(): AudienceMode {
    return this.audienceModeManager.getCurrentMode(this.selectedSegmentIds);
  }

  get showAudiencePreview(): boolean {
    return (
      this.args.config.audiencePreview && this.currentAudienceMode !== AUDIENCE_MODES.FIN_AUDIENCE
    );
  }

  get audienceActionProxy() {
    return {
      audienceMode: this.currentAudienceMode,
      selectedSegmentIds: this.selectedSegmentIds,
      switchToEveryone: this.switchToEveryone.bind(this),
      switchToCustom: this.switchToCustom.bind(this),
      toggleSegment: this.toggleSegment.bind(this),
      ruleset: this.args.ruleset,
    };
  }

  get shouldFilterUnsupportedAttributes() {
    // Filter unsupported attributes for inbound, outbound, and button bot workflows
    if (!this.args.customBot?.type) {
      return false;
    }

    let botType = this.args.customBot.type;
    return (
      botType === CUSTOM_BOT.BUTTON ||
      botType === CUSTOM_BOT.INBOUND ||
      botType === CUSTOM_BOT.OUTBOUND
    );
  }

  get shouldShowSegments(): boolean {
    return !this.hasTrackerTickets;
  }

  get shouldIncludeTicketAttributes(): boolean {
    // Triggerable custom bots need ticket attributes
    // Button/Inbound/Outbound bots don't need them
    if (this.args.customBot?.type) {
      let botType = this.args.customBot.type;
      return !(
        botType === CUSTOM_BOT.BUTTON ||
        botType === CUSTOM_BOT.INBOUND ||
        botType === CUSTOM_BOT.OUTBOUND
      );
    }
    // Default to true for triggerable bots and ticket workflows
    return true;
  }

  constructor(owner: unknown, args: any) {
    super(owner, args);
    this.audienceModeManager = new AudienceModeManager(this.args.ruleset);
    this.initializeAudienceState();
    this.args.config.contentEditorService.updateFinAudienceMode(true);
  }

  initializeAudienceState() {
    // Reset custom mode flag on initialization so we infer from saved data
    this.audienceModeManager.setCustomModeActive(false);

    // Fix role predicates to match the workflow type (migrate old data)
    this.migrateRolePredicatesToMatchWorkflowType();

    // Clear invalid audience settings for Tracker tickets FIRST
    this.ensureRolePredicatesMatchTicketType();

    // For new workflows with workflows-fin-audiences feature flag, set default "Everyone" role predicate
    if (this.shouldSetDefaultEveryoneAudience()) {
      this.setEveryoneRolePredicate();
    }
  }

  private migrateRolePredicatesToMatchWorkflowType() {
    // Migration: Fix workflows that have INVALID roles for their workflow type
    // For example, ticket workflows saved with visitor_role need it removed
    // BUT we should NOT force conversation workflows to have all 3 roles if user selected a subset
    let rolePredicates = this.ruleset?.rolePredicateGroup?.predicates;
    if (!rolePredicates || rolePredicates.length === 0) {
      return;
    }

    let rolePredicate = rolePredicates.objectAt(0);
    if (
      !rolePredicate ||
      rolePredicate.comparison !== 'in' ||
      !Array.isArray(rolePredicate.value)
    ) {
      return;
    }

    let currentRoles = [...rolePredicate.value];
    if (currentRoles.length === 0) {
      return;
    }

    // Get the allowed roles for this workflow type
    let allowedRoles = this.getAllowedRoleValues();
    if (allowedRoles.length === 0) {
      return;
    }

    // Check if any current roles are INVALID (not in the allowed list)
    let hasInvalidRoles = currentRoles.some((role) => !allowedRoles.includes(role));

    if (hasInvalidRoles) {
      // Remove invalid roles by keeping only the ones that are allowed
      let validRoles = currentRoles.filter((role) => allowedRoles.includes(role));

      if (validRoles.length > 0) {
        // Update with only the valid roles
        rolePredicate.set('value', validRoles);
      } else if (this.args.ruleset.rolePredicateGroup) {
        // If no valid roles remain, clear the role predicate group
        this.args.ruleset.set('rolePredicateGroup.predicates', []);
      }
    }
  }

  private shouldSetDefaultEveryoneAudience(): boolean {
    // Only set default if no existing audience configuration
    if (!this.args.config.inputs?.editorConfiguration?.supportsUserTargeting) {
      return false;
    }

    if (this.computedAllowedUserRoles.length === 0) {
      return false;
    }

    let hasNoSegments = this.selectedSegmentIds.length === 0;
    let hasNoRolePredicates = !this.ruleset?.rolePredicateGroup?.predicates?.length;
    let hasNoPredicates = !this.ruleset?.predicateGroup?.predicates?.length;

    return hasNoSegments && hasNoRolePredicates && hasNoPredicates;
  }

  private ensureRolePredicatesMatchTicketType() {
    if (!this.hasTrackerTickets) {
      return;
    }

    if (this.ruleset?.rolePredicateGroup?.predicates?.length) {
      this.ruleset.set('rolePredicateGroup.predicates', []);
    }

    if (this.ruleset?.segmentIds?.length > 0) {
      this.clearSegments();
    }

    this.audienceModeManager.setCustomModeActive(false);
  }

  @action
  switchToEveryone() {
    this.audienceModeManager.setCustomModeActive(false);
    this.clearSegments();
    this.clearPredicates();
    this.setEveryoneRolePredicate();
  }

  @action
  switchToCustom() {
    this.audienceModeManager.setCustomModeActive(true);
    this.clearSegments();
    this.initializePredicateGroups();
  }

  @action
  toggleSegment(segmentId: number) {
    this.audienceModeManager.setCustomModeActive(false);
    let newSegmentIds = this.toggleSegmentInList(segmentId, this.selectedSegmentIds);
    this.clearPredicates();
    this.args.ruleset.set('segmentIds', newSegmentIds);
  }

  private toggleSegmentInList(segmentId: number, currentIds: number[]): number[] {
    let newIds = [...currentIds];
    let index = newIds.indexOf(segmentId);

    if (index > -1) {
      newIds.splice(index, 1);
    } else {
      newIds.push(segmentId);
    }

    return newIds;
  }

  private clearSegments() {
    this.args.ruleset.set('segmentIds', []);
  }

  private clearPredicates() {
    if (this.args.ruleset.predicateGroup) {
      this.args.ruleset.set('predicateGroup.predicates', []);
    }
    if (this.args.ruleset.rolePredicateGroup) {
      this.args.ruleset.set('rolePredicateGroup.predicates', []);
    }
  }

  private setEveryoneRolePredicate() {
    let roleValues = this.getAllowedRoleValues();

    if (roleValues.length === 0) {
      return;
    }

    this.initializeRolePredicateGroup();
    let everyoneRolePredicate = this.store.createFragment('predicates/role', {
      attribute: 'role',
      type: 'role',
      comparison: 'in',
      value: roleValues,
      subclass: 'predicates/role',
    });
    this.args.ruleset.set('rolePredicateGroup.predicates', [everyoneRolePredicate]);
  }

  private getAllowedRoleValues(): string[] {
    let allowedRoles = this.computedAllowedUserRoles;
    let roleValues: string[] = [];

    if (allowedRoles.includes(audienceNames[audienceRoles.users])) {
      roleValues.push(audienceRoles.users);
    }
    if (allowedRoles.includes(audienceNames[audienceRoles.leads])) {
      roleValues.push(audienceRoles.leads);
    }
    if (allowedRoles.includes(audienceNames[audienceRoles.visitors])) {
      roleValues.push(audienceRoles.visitors);
    }

    return roleValues;
  }

  private initializePredicateGroups() {
    this.initializePredicateGroup();
    this.initializeRolePredicateGroup();
    this.clearPredicates();
  }

  private initializePredicateGroup() {
    if (!this.args.ruleset.predicateGroup) {
      this.args.ruleset.set(
        'predicateGroup',
        this.store.createFragment('predicates/predicate-group'),
      );
    }
  }

  private initializeRolePredicateGroup() {
    if (!this.args.ruleset.rolePredicateGroup) {
      this.args.ruleset.set(
        'rolePredicateGroup',
        this.store.createFragment('predicates/predicate-group'),
      );
    }
  }

  onUpdatePredicates(predicates: any) {
    this.updatePredicates(PREDICATE_PATHS.PREDICATES, predicates);
  }

  onUpdateRolePredicates(rolePredicates: any) {
    this.updatePredicates(PREDICATE_PATHS.ROLE_PREDICATES, rolePredicates);
  }

  get rulesEditorConfig() {
    let baseEditorConfiguration = this.args.config.inputs?.editorConfiguration;

    // Determine supportsUserTargeting:
    // - If tracker tickets: always false
    // - If editorConfiguration exists: use its value (undefined → false, explicit value → that value)
    // - If editorConfiguration is missing entirely: default to true (legacy behavior)
    let supportsUserTargeting;
    if (this.hasTrackerTickets) {
      supportsUserTargeting = false;
    } else if (baseEditorConfiguration !== undefined) {
      supportsUserTargeting = baseEditorConfiguration.supportsUserTargeting ?? false;
    } else {
      supportsUserTargeting = true;
    }

    // Always set these properties to ensure they're available for the dropdown
    let editorConfiguration = {
      workflowMatchingTargetingAttributeIdentifiers: this.getAttributeIdentifiers(),
      workflowMatchingTargetingAttributes: this.getAttributeGroups(),
      supportsConversationChannelTargeting: false,
      targetingAttributePickerLabel: this.intl.t(
        'operator.workflows.visual-builder.workflow-audience.add-audience',
      ),
      ...(baseEditorConfiguration || {}),
      supportsUserTargeting,
    };

    return {
      ...this.args.config,
      editorConfiguration,
      isDisabled: this.args.isDisabled,
      allowedUserRoles: this.computedAllowedUserRoles,
    };
  }

  getAttributeIdentifiers() {
    // Extract all identifiers from the comprehensive attribute groups
    return this.getAttributeGroups()
      .flatMap((group) => group.attributes || [])
      .map((attr) => attr.identifier)
      .flatMap((identifier) =>
        identifier.startsWith('user_event_summaries.')
          ? [`${identifier}.count`, `${identifier}.first`, `${identifier}.last`]
          : identifier,
      )
      .concat(['role']);
  }

  getAttributeGroups(): AttributeGroupList {
    let builder = new AttributeGroupBuilder({
      attributeService: this.attributeService,
      appService: this.appService,
      additionalAttributes: this.args.config.inputs?.additionalAttributes || [],
      // Use config value if explicitly set, otherwise determine based on bot type
      includeTicketAttributeGroups:
        this.args.config.inputs?.includeTicketAttributeGroups ?? this.shouldIncludeTicketAttributes,
    });

    return builder.getAttributeGroups();
  }

  private updatePredicates(path: string, predicates: any) {
    this.ruleset.set(path, copy(predicates));
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Workflows::AudienceSelectorWrapper': typeof WorkflowAudienceSelectorWrapper;
    'workflows/audience-selector-wrapper': typeof WorkflowAudienceSelectorWrapper;
  }
}
